/**
 * /admin/reservas — Admin panel for the Starlink Mini group-buy campaign.
 *
 * Features:
 *   • KPI cards (counter, revenue collected, pending balance, projected revenue)
 *   • Filter by status, search by email / order number
 *   • Inline status editor (dropdown per row) with instant PATCH
 *   • Refund button (Stripe refund)
 *   • Campaign config editor: fecha_cierre, objetivo, importes, imágenes/vídeos, toggles
 *
 * Reuses the same admin token as /admin (localStorage key: `adminToken`).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowLeft, RefreshCw, Save, Filter, Search, ExternalLink,
  CheckCircle2, PackageCheck, Truck, XCircle, RotateCcw, LogOut,
  BadgeEuro, ShieldCheck, Target, TrendingUp, Loader2, Settings2,
  Mail,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("adminToken");
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fmtEUR = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

const STATUSES = [
  { key: "pending_payment", label: "Pendiente pago", color: "bg-white/5 text-white/50" },
  { key: "reservado", label: "Reservado", color: "bg-blue-500/15 text-blue-300" },
  { key: "pedido_cursado", label: "Pedido cursado", color: "bg-yellow-500/15 text-yellow-300" },
  { key: "saldo_pagado", label: "Saldo pagado", color: "bg-purple-500/15 text-purple-300" },
  { key: "enviado", label: "Enviado", color: "bg-emerald-500/15 text-emerald-300" },
  { key: "cancelado", label: "Cancelado", color: "bg-red-500/15 text-red-300" },
  { key: "reembolsado", label: "Reembolsado", color: "bg-red-500/15 text-red-300" },
];
const statusLabel = (k) => STATUSES.find((s) => s.key === k)?.label || k;
const statusColor = (k) => STATUSES.find((s) => s.key === k)?.color || "bg-white/5 text-white/50";

export default function AdminStarlinkReservations() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reservations, setReservations] = useState([]);
  const [totals, setTotals] = useState({ reservas_actuales: 0, objetivo_reservas: 100, recaudado: 0, saldos_pendientes: 0, ingreso_previsto: 0 });
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [config, setConfig] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rs, cf] = await Promise.all([
        axios.get(`${API}/starlink/admin/reservations`, authHeaders()),
        axios.get(`${API}/starlink/admin/config`, authHeaders()),
      ]);
      setReservations(rs.data.reservations || []);
      setTotals(rs.data.totals || totals);
      setConfig(cf.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("adminToken");
        navigate("/admin");
      } else {
        toast.error("Error cargando panel: " + (e?.response?.data?.detail || e.message));
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!getToken()) { navigate("/admin"); return; }
    load();
  }, [load, navigate]);

  const filtered = useMemo(() => {
    return reservations.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          (r.email || "").toLowerCase().includes(q) ||
          (r.order_number || "").toLowerCase().includes(q) ||
          (r.nombre || "").toLowerCase().includes(q) ||
          (r.apellidos || "").toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [reservations, statusFilter, search]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/starlink/admin/reservations/${id}`, { status }, authHeaders());
      toast.success("Estado actualizado");
      load();
    } catch (e) {
      toast.error("No se pudo actualizar: " + (e?.response?.data?.detail || e.message));
    }
  };

  const refund = async (r) => {
    if (!window.confirm(`¿Reembolsar la reserva ${r.order_number} (${fmtEUR(r.importe_reserva)})?\nLa devolución será inmediata vía Stripe.`)) return;
    try {
      await axios.post(`${API}/starlink/admin/reservations/${r.id}/refund`, {}, authHeaders());
      toast.success("Reembolso procesado");
      load();
    } catch (e) {
      toast.error("No se pudo reembolsar: " + (e?.response?.data?.detail || e.message));
    }
  };

  const sendBalanceLink = async (r) => {
    const already = r.balance_link_sent_at;
    const msg = already
      ? `Ya se envió un link de saldo a ${r.email} el ${new Date(already).toLocaleDateString("es-ES")}. ¿Reenviar (se creará una NUEVA sesión de Stripe)?`
      : `Crear el enlace de pago del saldo (${fmtEUR(r.importe_saldo)}) y enviarlo por email a ${r.email}?`;
    if (!window.confirm(msg)) return;
    try {
      const resp = await axios.post(`${API}/starlink/admin/reservations/${r.id}/send-balance-link`, {}, authHeaders());
      toast.success(resp.data?.email_sent ? "Enlace de saldo enviado por email ✓" : "Enlace creado (email no enviado — revisa SMTP)");
      load();
    } catch (e) {
      toast.error("No se pudo enviar: " + (e?.response?.data?.detail || e.message));
    }
  };

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      const body = {
        objetivo_reservas: Number(config.objetivo_reservas),
        fecha_cierre: config.fecha_cierre,
        precio_total: Number(config.precio_total),
        importe_reserva: Number(config.importe_reserva),
        importe_saldo: Number(config.importe_saldo),
        plazo_entrega: config.plazo_entrega,
        plazo_maximo_legal: config.plazo_maximo_legal,
        campana_activa: !!config.campana_activa,
        lote_cerrado_manual: !!config.lote_cerrado_manual,
        og_image_url: config.og_image_url || "",
        video_hero_url: config.video_hero_url || "",
        imagen_producto_url: config.imagen_producto_url || "",
        imagen_prueba_url: config.imagen_prueba_url || "",
      };
      const r = await axios.patch(`${API}/starlink/admin/config`, body, authHeaders());
      setConfig(r.data);
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error("Error guardando: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSavingCfg(false);
    }
  };

  const checkExpiry = async () => {
    if (!window.confirm("¿Ejecutar la verificación de cierre?\n\nSi la fecha de cierre ya pasó y no llegamos a las reservas objetivo, reembolsará automáticamente TODAS las reservas pagadas vía Stripe. Esta acción no se puede deshacer.")) return;
    try {
      const r = await axios.post(`${API}/starlink/admin/check-expiry`, {}, authHeaders());
      const d = r.data || {};
      if (d.resolved === "auto_refunded") {
        toast.success(`Campaña cerrada · ${d.refunded} reservas reembolsadas${d.failed ? ` (${d.failed} fallos)` : ""}`);
      } else if (d.resolved === "goal_reached") {
        toast.success(`Objetivo alcanzado (${d.counter}/${d.objetivo}) · campaña resuelta con éxito`);
      } else if (d.skipped === "not_expired") {
        toast.info(`Aún faltan ${d.days_left} día(s) para la fecha de cierre.`);
      } else if (d.skipped === "already_resolved") {
        toast.info("La campaña ya estaba resuelta.");
      } else {
        toast.info(`Skipped: ${d.skipped || "sin cambios"}`);
      }
      load();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    }
  };

  const pct = Math.min(100, (totals.reservas_actuales / (totals.objetivo_reservas || 100)) * 100);

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white noise" data-testid="admin-reservas-page">
      {/* Header */}
      <header className="border-b border-white/8 bg-[#0A0A0F]/90 backdrop-blur-lg sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2 text-white/70 hover:text-white text-sm">
              <ArrowLeft className="w-4 h-4" /> Admin
            </Link>
            <span className="text-white/20">·</span>
            <h1 className="text-sm font-bold tracking-tight">Reservas Starlink Mini</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="text-white/60 hover:text-white text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md hover:bg-white/5">
              <RefreshCw className="w-3.5 h-3.5" /> Refrescar
            </button>
            <button onClick={() => { localStorage.removeItem("adminToken"); navigate("/admin"); }} className="text-white/50 hover:text-red-300 text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md hover:bg-white/5">
              <LogOut className="w-3.5 h-3.5" /> Salir
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8 space-y-8">
        {/* KPIs */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI icon={<Target className="w-4 h-4" />} label="Reservas activas" value={`${totals.reservas_actuales} / ${totals.objetivo_reservas}`} sub={`${pct.toFixed(0)}% del objetivo`} highlight />
          <KPI icon={<BadgeEuro className="w-4 h-4" />} label="Recaudado (30%)" value={fmtEUR(totals.recaudado)} sub="Depósitos cobrados" />
          <KPI icon={<ShieldCheck className="w-4 h-4" />} label="Saldos pendientes" value={fmtEUR(totals.saldos_pendientes)} sub="A cobrar antes del envío" />
          <KPI icon={<TrendingUp className="w-4 h-4" />} label="Ingreso previsto" value={fmtEUR(totals.ingreso_previsto)} sub="Cuando se envíe todo" />
        </section>

        {/* Reservations table */}
        <section className="rounded-2xl border border-white/10 bg-[#0F0F16] overflow-hidden">
          <div className="p-4 lg:p-5 border-b border-white/10 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-sm font-bold">Reservas</div>
              <div className="text-xs text-white/40 mt-0.5">{filtered.length} de {reservations.length}</div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-white/40 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar email / pedido / nombre"
                  data-testid="reservas-search"
                  className="pl-8 pr-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-blue-500/50 w-64"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5 text-white/40" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  data-testid="reservas-filter"
                  className="text-sm bg-white/5 border border-white/10 rounded-lg px-2 py-2 focus:outline-none focus:border-blue-500/50"
                >
                  <option value="all">Todos</option>
                  {STATUSES.map((s) => <option key={s.key} value={s.key} className="bg-[#0F0F16]">{s.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="p-16 flex items-center justify-center text-white/40 text-sm gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center text-white/40 text-sm">Sin reservas para mostrar.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-white/50">
                  <tr>
                    <Th>Pedido</Th>
                    <Th>Cliente</Th>
                    <Th>Contacto</Th>
                    <Th>Dirección</Th>
                    <Th>Reserva</Th>
                    <Th>Estado</Th>
                    <Th>Fecha</Th>
                    <Th>Acciones</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} className="border-t border-white/8 hover:bg-white/[0.02]" data-testid={`row-${r.order_number}`}>
                      <td className="px-4 py-3 mono font-bold text-blue-300 whitespace-nowrap">{r.order_number}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{r.nombre} {r.apellidos}</div>
                        {r.nif && <div className="text-[11px] text-white/40">{r.nif}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-white/80">{r.email}</div>
                        <div className="text-[11px] text-white/40">{r.telefono}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-white/60">
                        <div>{r.direccion_calle} {r.direccion_puerta}</div>
                        <div>{r.codigo_postal} · {r.poblacion} · {r.provincia}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-bold text-emerald-300">{fmtEUR(r.importe_reserva)}</div>
                        <div className="text-[11px] text-white/40">+ {fmtEUR(r.importe_saldo)} saldo</div>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={r.status}
                          onChange={(e) => setStatus(r.id, e.target.value)}
                          data-testid={`status-${r.order_number}`}
                          className={`text-xs font-bold uppercase tracking-widest rounded-full px-3 py-1.5 border-0 focus:outline-none focus:ring-1 focus:ring-blue-500/50 ${statusColor(r.status)}`}
                        >
                          {STATUSES.map((s) => <option key={s.key} value={s.key} className="bg-[#0F0F16] text-white normal-case">{s.label}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-[11px] text-white/50 whitespace-nowrap">
                        {new Date(r.created_at).toLocaleDateString("es-ES")}<br />
                        <span className="text-white/30">{new Date(r.created_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {(r.status === "reservado" || r.status === "pedido_cursado") && (
                            <button
                              type="button"
                              onClick={() => sendBalanceLink(r)}
                              data-testid={`balance-${r.order_number}`}
                              title={r.balance_link_sent_at ? `Reenviar link de saldo (enviado ${new Date(r.balance_link_sent_at).toLocaleDateString("es-ES")})` : "Enviar link de saldo por email"}
                              className={`p-1.5 rounded-md hover:bg-amber-500/10 ${r.balance_link_sent_at ? "text-amber-300" : "text-white/50 hover:text-amber-300"}`}
                            >
                              <Mail className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {r.payment_intent_id && r.status !== "reembolsado" && (
                            <button
                              type="button"
                              onClick={() => refund(r)}
                              data-testid={`refund-${r.order_number}`}
                              title="Reembolsar Stripe"
                              className="p-1.5 rounded-md hover:bg-red-500/10 text-white/50 hover:text-red-300"
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <a
                            href={`mailto:${r.email}?subject=Reserva ${r.order_number}`}
                            className="p-1.5 rounded-md hover:bg-white/5 text-white/50 hover:text-white"
                            title="Enviar email"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Config editor */}
        {config && (
          <motion.section
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-white/10 bg-[#0F0F16] p-5 lg:p-6"
            data-testid="config-editor"
          >
            <div className="flex items-center gap-2 mb-5">
              <Settings2 className="w-4 h-4 text-blue-300" />
              <h2 className="text-sm font-bold">Configuración de la campaña</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <CfgField label="Objetivo de reservas" type="number" value={config.objetivo_reservas} onChange={(v) => setConfig({ ...config, objetivo_reservas: v })} testid="cfg-objetivo" />
              <CfgField label="Fecha de cierre (ISO)" value={config.fecha_cierre || ""} onChange={(v) => setConfig({ ...config, fecha_cierre: v })} placeholder="2026-03-31T23:59:00Z" testid="cfg-fecha" />
              <CfgField label="Precio total (€)" type="number" step="0.01" value={config.precio_total} onChange={(v) => setConfig({ ...config, precio_total: v })} testid="cfg-precio" />
              <CfgField label="Importe reserva (€)" type="number" step="0.01" value={config.importe_reserva} onChange={(v) => setConfig({ ...config, importe_reserva: v })} testid="cfg-reserva" />
              <CfgField label="Importe saldo (€)" type="number" step="0.01" value={config.importe_saldo} onChange={(v) => setConfig({ ...config, importe_saldo: v })} testid="cfg-saldo" />
              <CfgField label="Plazo de entrega" value={config.plazo_entrega} onChange={(v) => setConfig({ ...config, plazo_entrega: v })} testid="cfg-plazo" />
              <CfgField label="Plazo máximo legal" value={config.plazo_maximo_legal} onChange={(v) => setConfig({ ...config, plazo_maximo_legal: v })} testid="cfg-plazo-max" />
              <CfgField label="Imagen producto (URL)" value={config.imagen_producto_url || ""} onChange={(v) => setConfig({ ...config, imagen_producto_url: v })} testid="cfg-img-producto" />
              <CfgField label="Imagen prueba humana (URL)" value={config.imagen_prueba_url || ""} onChange={(v) => setConfig({ ...config, imagen_prueba_url: v })} testid="cfg-img-prueba" />
              <CfgField label="Vídeo hero (URL)" value={config.video_hero_url || ""} onChange={(v) => setConfig({ ...config, video_hero_url: v })} testid="cfg-video" />
              <CfgField label="OG Image (URL — compartir en redes)" value={config.og_image_url || ""} onChange={(v) => setConfig({ ...config, og_image_url: v })} testid="cfg-og" />
            </div>

            <div className="flex items-center gap-6 mt-5">
              <CfgToggle label="Campaña visible en home" checked={!!config.campana_activa} onChange={(v) => setConfig({ ...config, campana_activa: v })} testid="cfg-activa" />
              <CfgToggle label="Lote cerrado manualmente" checked={!!config.lote_cerrado_manual} onChange={(v) => setConfig({ ...config, lote_cerrado_manual: v })} testid="cfg-cerrado" />
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button onClick={checkExpiry} data-testid="cfg-check-expiry" className="border border-orange-500/40 text-orange-300 hover:bg-orange-500/10 rounded-lg px-4 py-2.5 text-sm font-bold inline-flex items-center gap-2">
                <RotateCcw className="w-4 h-4" />
                Verificar cierre + auto-reembolso
              </button>
              <button onClick={saveConfig} disabled={savingCfg} data-testid="cfg-save" className="bg-blue-500 hover:bg-blue-400 disabled:bg-white/10 text-white rounded-lg px-5 py-2.5 text-sm font-bold inline-flex items-center gap-2">
                {savingCfg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Guardar configuración
              </button>
            </div>
          </motion.section>
        )}
      </div>
    </div>
  );
}

function KPI({ icon, label, value, sub, highlight = false }) {
  return (
    <div className={`rounded-xl border p-4 lg:p-5 ${highlight ? "border-blue-500/40 bg-blue-500/[0.06]" : "border-white/10 bg-white/[0.02]"}`}>
      <div className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest mono ${highlight ? "text-blue-300" : "text-white/40"}`}>
        {icon} {label}
      </div>
      <div className="text-2xl lg:text-3xl font-black tracking-tight mt-2">{value}</div>
      {sub && <div className="text-[11px] text-white/40 mt-1">{sub}</div>}
    </div>
  );
}

function Th({ children }) { return <th className="text-left px-4 py-3 font-bold">{children}</th>; }

function CfgField({ label, value, onChange, type = "text", step, placeholder, testid }) {
  return (
    <label className="block">
      <span className="block text-[11px] text-white/50 mb-1.5">{label}</span>
      <input
        type={type}
        step={step}
        value={value ?? ""}
        onChange={(e) => onChange(type === "number" ? e.target.value : e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60"
      />
    </label>
  );
}

function CfgToggle({ label, checked, onChange, testid }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
        className="w-4 h-4 accent-blue-500"
      />
      <span className="text-white/80">{label}</span>
    </label>
  );
}
